
import { useState } from 'react';
import { getAggregatedStats, getSessionMetrics, getExitPages, getUTMStats } from '@/lib/analytics';
import { fetchStatWithErrorHandling, fetchStatsWithErrorHandling, fetchReferrerStats } from './statsUtils';
import { adminLog } from '@/lib/utils/admin-logger';
import { AuthFilter } from '@/components/analytics/AuthFilterButtons';
import { ReferrerStat, SessionMetrics, ExitPageStat, UTMStat } from '@/lib/analytics/types';

export type LoadingStates = {
  loadingPageStats: boolean;
  loadingActionStats: boolean;
  loadingDailyStats: boolean;
  loadingPlatformStats: boolean;
  loadingBrowserStats: boolean;
  loadingOSStats: boolean;
  loadingTimeStats: boolean;
  loadingLanguageStats: boolean;
  loadingTotalStats: boolean;
  loadingReferrerStats: boolean;
  loadingSessionMetrics: boolean;
  loadingExitPages: boolean;
  loadingUTMStats: boolean;
};

export type ErrorHandlers = {
  onPageStatsError: (error: Error | null) => void;
  onActionStatsError: (error: Error | null) => void;
  onDailyStatsError: (error: Error | null) => void;
  onPlatformStatsError: (error: Error | null) => void;
  onBrowserStatsError: (error: Error | null) => void;
  onOSStatsError: (error: Error | null) => void;
  onTimeStatsError: (error: Error | null) => void;
  onLanguageStatsError: (error: Error | null) => void;
  onReferrerStatsError: (error: Error | null) => void;
  onSessionMetricsError: (error: Error | null) => void;
  onExitPagesError: (error: Error | null) => void;
  onUTMStatsError: (error: Error | null) => void;
};

export const useFetchStats = (selectedRange: number, authFilter: AuthFilter) => {
  // Data states
  const [pageStats, setPageStats] = useState<any[]>([]);
  const [actionStats, setActionStats] = useState<any[]>([]);
  const [dailyStats, setDailyStats] = useState<any[]>([]);
  const [platformStats, setPlatformStats] = useState<any[]>([]);
  const [browserStats, setBrowserStats] = useState<any[]>([]);
  const [osStats, setOSStats] = useState<any[]>([]);
  const [timeStats, setTimeStats] = useState<any[]>([]);
  const [languageStats, setLanguageStats] = useState<any[]>([]);
  const [referrerStats, setReferrerStats] = useState<ReferrerStat[]>([]);
  const [sessionMetrics, setSessionMetrics] = useState<SessionMetrics | null>(null);
  const [exitPages, setExitPages] = useState<ExitPageStat[]>([]);
  const [utmStats, setUTMStats] = useState<UTMStat[]>([]);

  // Individual loading states
  const [loadingPageStats, setLoadingPageStats] = useState(true);
  const [loadingActionStats, setLoadingActionStats] = useState(true);
  const [loadingDailyStats, setLoadingDailyStats] = useState(true);
  const [loadingPlatformStats, setLoadingPlatformStats] = useState(true);
  const [loadingBrowserStats, setLoadingBrowserStats] = useState(true);
  const [loadingOSStats, setLoadingOSStats] = useState(true);
  const [loadingTimeStats, setLoadingTimeStats] = useState(true);
  const [loadingLanguageStats, setLoadingLanguageStats] = useState(true);
  const [loadingTotalStats, setLoadingTotalStats] = useState(true);
  const [loadingReferrerStats, setLoadingReferrerStats] = useState(false);
  const [loadingSessionMetrics, setLoadingSessionMetrics] = useState(true);
  const [loadingExitPages, setLoadingExitPages] = useState(true);
  const [loadingUTMStats, setLoadingUTMStats] = useState(true);

  // Fetch all stats
  const fetchAllStats = async (
    startDate: Date, 
    endDate: Date,
    errorHandlers: ErrorHandlers
  ) => {
    adminLog(`Fetching analytics data for range: ${selectedRange} days with auth filter: ${authFilter}`);
    
    // Track all fetch promises
    const fetchPromises = [];
    
    // Fetch page stats
    fetchPromises.push(
      fetchStatWithErrorHandling(
        async () => {
          return await getAggregatedStats(startDate, endDate, 'page_path', authFilter);
        },
        setPageStats,
        setLoadingPageStats,
        errorHandlers.onPageStatsError,
        'page statistics'
      )
    );
    
    // Fetch action stats
    fetchPromises.push(
      fetchStatWithErrorHandling(
        async () => {
          return await getAggregatedStats(startDate, endDate, 'action_type', authFilter);
        },
        setActionStats,
        setLoadingActionStats,
        errorHandlers.onActionStatsError,
        'action statistics'
      )
    );
    
    // Fetch daily stats
    fetchPromises.push(
      fetchStatWithErrorHandling(
        async () => {
          return await getAggregatedStats(startDate, endDate, 'daily', authFilter);
        },
        setDailyStats,
        setLoadingDailyStats,
        errorHandlers.onDailyStatsError,
        'daily statistics'
      )
    );
    
    // Fetch platform stats
    fetchPromises.push(
      fetchStatWithErrorHandling(
        async () => {
          return await getAggregatedStats(startDate, endDate, 'platform', authFilter);
        },
        setPlatformStats,
        setLoadingPlatformStats,
        errorHandlers.onPlatformStatsError,
        'platform statistics'
      )
    );
    
    // Fetch browser stats
    fetchPromises.push(
      fetchStatWithErrorHandling(
        async () => {
          return await getAggregatedStats(startDate, endDate, 'browser', authFilter);
        },
        setBrowserStats,
        setLoadingBrowserStats,
        errorHandlers.onBrowserStatsError,
        'browser statistics'
      )
    );
    
    // Fetch OS stats
    fetchPromises.push(
      fetchStatWithErrorHandling(
        async () => {
          return await getAggregatedStats(startDate, endDate, 'os', authFilter);
        },
        setOSStats,
        setLoadingOSStats,
        errorHandlers.onOSStatsError,
        'OS statistics'
      )
    );
    
    // Fetch time stats
    fetchPromises.push(
      fetchStatWithErrorHandling(
        async () => {
          return await getAggregatedStats(startDate, endDate, 'time', authFilter);
        },
        setTimeStats,
        setLoadingTimeStats,
        errorHandlers.onTimeStatsError,
        'time statistics'
      )
    );
    
    // Fetch language stats
    fetchPromises.push(
      fetchStatWithErrorHandling(
        async () => {
          return await getAggregatedStats(startDate, endDate, 'language', authFilter);
        },
        setLanguageStats,
        setLoadingLanguageStats,
        errorHandlers.onLanguageStatsError,
        'language statistics'
      )
    );
    
    // Add referrer stats fetching
    fetchPromises.push(
      fetchStatWithErrorHandling(
        () => fetchReferrerStats(startDate, endDate, authFilter),
        setReferrerStats,
        setLoadingReferrerStats,
        errorHandlers.onReferrerStatsError || (() => {}),
        'referrer statistics'
      )
    );
    
    // Fetch session metrics
    fetchPromises.push(
      fetchStatWithErrorHandling(
        async () => {
          return await getSessionMetrics(startDate, endDate, authFilter);
        },
        setSessionMetrics,
        setLoadingSessionMetrics,
        errorHandlers.onSessionMetricsError,
        'session metrics'
      )
    );
    
    // Fetch exit pages
    fetchPromises.push(
      fetchStatWithErrorHandling(
        async () => {
          return await getExitPages(startDate, endDate, authFilter);
        },
        setExitPages,
        setLoadingExitPages,
        errorHandlers.onExitPagesError,
        'exit pages'
      )
    );
    
    // Fetch UTM stats
    fetchPromises.push(
      fetchStatWithErrorHandling(
        async () => {
          return await getUTMStats(startDate, endDate, authFilter);
        },
        setUTMStats,
        setLoadingUTMStats,
        errorHandlers.onUTMStatsError,
        'UTM statistics'
      )
    );
    
    // Wait for all fetches to complete
    const results = await Promise.allSettled(fetchPromises);
    adminLog(`Analytics data fetch completed: ${results.filter(r => r.status === 'fulfilled').length} of ${results.length} successful`);
  };

  // Reset all loading states
  const resetLoadingStates = () => {
    setLoadingPageStats(true);
    setLoadingActionStats(true);
    setLoadingDailyStats(true);
    setLoadingPlatformStats(true);
    setLoadingBrowserStats(true);
    setLoadingOSStats(true);
    setLoadingTimeStats(true);
    setLoadingLanguageStats(true);
    setLoadingTotalStats(true);
    setLoadingReferrerStats(true);
    setLoadingSessionMetrics(true);
    setLoadingExitPages(true);
    setLoadingUTMStats(true);
  };

  return {
    // Data
    pageStats,
    actionStats,
    dailyStats,
    platformStats,
    browserStats,
    osStats,
    timeStats,
    languageStats,
    referrerStats,
    sessionMetrics,
    exitPages,
    utmStats,
    
    // Loading states - consolidated
    loadingStats: {
      loadingPageStats,
      loadingActionStats,
      loadingDailyStats,
      loadingPlatformStats,
      loadingBrowserStats,
      loadingOSStats,
      loadingTimeStats,
      loadingLanguageStats,
      loadingTotalStats,
      loadingReferrerStats,
      loadingSessionMetrics,
      loadingExitPages,
      loadingUTMStats,
    },
    
    // Functions
    fetchAllStats,
    resetLoadingStates
  };
};
